import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BREAKPOINTS, ROUTER_LINKS } from '../../common/consts/app-keys.const';
import { useQuery } from '@tanstack/react-query';
import { homeService } from '../service/home.service';
import BlackButton from '../../settings/ui/black-button';
import DemoVideo from './DemoVideo';
import { useTheme } from '../../common/hooks/use-theme';

interface IProps {
  onClick: () => void;
  touchposeVersion?: string;
}

export const ProductDemoSection = ({ onClick, touchposeVersion = '' }: IProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const handleBuySubscription = () => {
    onClick();
  };
  const productQuery = useQuery({
    queryKey: ['products'],
    queryFn: async () => homeService.getProduct()
  });

  const product = productQuery.isSuccess
    ? {
        name: productQuery.data!.name,
        description: productQuery.data!.description
      }
    : {
        name: 'Loading',
        description: t('home.productDescriptionFallback')
      };

  const [isSmallScreen, setIsSmallScreen] = useState(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleVideoPlayPause = () => {
    if (!videoRef.current) return;

    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPaused(false);
    } else {
      videoRef.current.pause();
      setIsPaused(true);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= BREAKPOINTS.tablet);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="pt-10 px-[8px] md:px-10 laptop_l:min-h-max max-w-[1080px] mx-auto text-center">
      <div className="w-full flex justify-center align-middle flex-col">
        <div className="mb-8">
          <span
            onClick={() => navigate(ROUTER_LINKS.TOUCHPOSE_CHANGELOG)}
            className="inline-flex items-center gap-[6px] py-1 px-3 rounded-full bg-surface-hover border border-blue-100 text-[11px] font-mono text-blue tracking-[0.08em] cursor-pointer hover:bg-surface-hover-section transition-colors"
          >
            <span className="w-[5px] h-[5px] rounded-full bg-blue animate-pulse shrink-0" />
            {t('home.nowAvailable', { version: touchposeVersion })}
          </span>
        </div>
        <h1 className="px-[16px] md:px-0 mx-auto mb-[13px] mt-6 flex justify-center">
          <span className="relative max-w-full">
            <img
              src={`${process.env.PUBLIC_URL || ''}/touchpose_logo.svg`}
              alt="TouchPose: Animate with Ease"
              className={`block h-[115px] sm:h-[144px] md:h-[173px] lg:h-[202px] xl:h-[230px] w-auto transition-opacity duration-300 ${isDark ? 'opacity-0' : 'opacity-100'}`}
            />
            <img
              src={`${process.env.PUBLIC_URL || ''}/touchpose_logo_dark.svg`}
              alt=""
              aria-hidden="true"
              className={`absolute inset-0 h-full w-auto transition-opacity duration-300 ${isDark ? 'opacity-100' : 'opacity-0'}`}
            />
          </span>
        </h1>
        <p className="text-center px-[16px] md:px-0 max-w-[776px] font-body text-[18px] mx-auto text-content-muted-alt font-normal tracking-[-0.01em] mb-9">
          {t('home.animate')}{' '}
          <span className="font-medium text-content-dark not-italic">
            {t('home.animateDirectly')}
          </span>{' '}
          {t('home.touchPoseGo')}
        </p>

        {/* The rig library came out of the nav bar and landed here, next to the
            trial: it is TouchPose's library, and this is where someone is
            already deciding whether to try TouchPose. Secondary weight — the
            trial is still the ask. Stacks below the phone breakpoint so
            neither button gets squeezed. */}
        <div className="mb-14 flex flex-col sm:flex-row items-center justify-center gap-[12px] sm:gap-[16px]">
          {/* BlackButton centres itself with mx-auto, which as a flex child
              eats the free space and shoves its sibling to the far edge. The
              wrapper shrink-wraps it so the gap does the spacing instead. */}
          <div className="shrink-0">
            <BlackButton />
          </div>
          <button
            type="button"
            onClick={() => navigate(ROUTER_LINKS.MODELS)}
            className="flex justify-center items-center gap-[8px] min-w-[268px] sm:min-w-0 h-[52px] px-[24px] rounded-[32px] border border-border-brand bg-transparent text-content-dark font-body text-medium text-[18px]
            transition-all duration-300 hover:bg-surface-hover hover:text-blue hover:gap-[12px]"
          >
            {t('products.exploreLibrary')}
            <span aria-hidden="true">→</span>
          </button>
        </div>

        <DemoVideo />
      </div>
    </div>
  );
};
